import { Grid } from "@mui/material";
import LaddaButton from "../../../components/buttons/laddaButton";
import ModalComponent from "../../../components/modals";
import FormTextField from "../../../components/textField/formTextField";
import SimpleSelect from "../../../components/selects/simpleSelect";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import FormCalendar from "../../../components/calendar/formCalendar";
import FileUploadButton from "../../../components/fileUploadBtn";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import { formatCustomDateName, parseDateAsLocal } from "../../../utils/dateUtils";
import {
  addOrUpdateImportRegistration,
  callApiAsync,
  checkImportRegistrationDuplicate,
  getSuppliersForeignTrade
} from "../../../services/apiService";
import { icons } from "../../../constants";
import AddSupplier from "./addSuppliers";
import ForeignTradeVerificationModal from "../../../components/modals/verificationModal/foreignTradeVerification";
import * as yup from "yup";
import ForeignTradeUpdateWarningModal from "../../../components/modals/verificationModal/ForeignTradeUpdateWarningModal";

const ImportRegistrationCreateUpdateModal = ({
  open,
  handleClose,
  row,
  onShowToast,
  getTable
}) => {
  const validationSchema = yup.object({
    registration_number: yup
      .string()
      .required("El número de registro es obligatorio"),
    supplier_id: yup.string().required("El proveedor es obligatorio"),
    product: yup.string().required("El producto es obligatorio"),
    start_date: yup.date().required("La fecha de inicio es obligatoria"),
    end_date: yup.date().required("La fecha de fin es obligatoria")
  });

  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState("");
  const [fileType, setFileType] = useState(null);
  const [addSuppliersModal, setAddSuppliersModal] = useState(false);
  const [selectData, setSelectData] = useState([]);
  const [openVerification, setOpenVerification] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  const [existingCheck, setExistingCheck] = useState({
    open: false,
    data: null
  });

  const {
    control,
    getValues,
    formState: { errors },
    reset,
    trigger
  } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const isEdit = row?.id ? true : false;

  const onSubmit = async () => {
    const values = getValues();

    const payload = new FormData();

    const startDate = getValues("start_date") || null; 
    const endDate = getValues("end_date") || null;

    payload.append("registration_number", values.registration_number);
    payload.append("supplier_id", values.supplier_id);
    payload.append("product", values.product);
    payload.append("start_date", startDate ? format(new Date(startDate), "yyyy-MM-dd") : "");
    payload.append("end_date", endDate ? format(new Date(endDate), "yyyy-MM-dd") : "");
    payload.append("file", file ? file : null);

    if (row?.id) {
      payload.append("id", row.id);
    }

    await callApiAsync(
      () => addOrUpdateImportRegistration(payload),
      () => {
        getTable();
        onShowToast?.(
          "success",
          row?.id ? "Registro actualizado correctamente." : "Registro creado correctamente."
        );
      }
    );

    handleClose();
  };

  const submitFromModal = async (modalData) => {
    const hasWrapper =
      modalData && typeof modalData === "object" && "form" in modalData;
    const f = hasWrapper ? modalData.form : modalData;
    const previousId = hasWrapper ? modalData.id : f?.previous_id;

    const fd = new FormData();
    fd.append("registration_number", f?.registration_number?.trim() ?? "");
    fd.append("supplier_id", f?.supplier_id ?? "");
    fd.append("product", f?.product?.trim() ?? "");
    fd.append("start_date", f?.start_date ? format(new Date(f.start_date), "yyyy-MM-dd") : "");
    fd.append("end_date", f?.end_date ? format(new Date(f.end_date), "yyyy-MM-dd") : "");
    fd.append("file", file ? file : null);

    if (previousId) {
      fd.append("id", previousId);
    }

    await callApiAsync(
      () => addOrUpdateImportRegistration(fd),
      () => {
        getTable();
        onShowToast?.(
          "success",
          previousId ? "Registro actualizado correctamente." : "Registro creado correctamente."
        );
      }
    );

    handleClose();
  };

  const openVerificationModal = async () => {
    const ok = await trigger();
    if (!ok) return;

    const v = getValues();

    const snapshot = {
      registration_number: v.registration_number?.trim() ?? "",
      supplier_id: v.supplier_id ?? null,
      product: v.product?.trim() ?? "",
      start_date: formatCustomDateName(v.start_date),
      end_date: formatCustomDateName(v.end_date),
      file_preview_url: fileUrl || null,
      file_type: fileType || null,
      is_edit: Boolean(row?.id),
      previous_id: row?.id ?? null
    };

    callApiAsync(
      () =>
        checkImportRegistrationDuplicate(
          snapshot.supplier_id,
          snapshot.product
        ),
      (data) => {
        const exists = data?.exists;
        const existingRec = data?.existing ?? null;

        if (exists && existingRec) {
          const prepared = {
            id: existingRec.id ?? null,
            form: {
              ...snapshot,
              start_date: v.start_date,
              end_date: v.end_date
            },
            formDisplay: {
              start_date: snapshot.start_date,
              end_date: snapshot.end_date
            }
          };
          setExistingCheck({ open: true, data: prepared });
          return;
        } else {
          setVerificationData(snapshot);
          setOpenVerification(true);
        }
      }
    );
  };

  const getSelectSuppliers = () => {
    callApiAsync(
      () => getSuppliersForeignTrade(),
      (data) => {
        setSelectData(data);
      }
    );
  };

  const setModalValues = () => {
    if (row) {
      reset({
        registration_number: row.registration_number ?? "",
        supplier_id: row.supplier_id ?? null,
        product: row.product ?? "",
        start_date: row.start_date ? parseDateAsLocal(String(row.start_date).slice(0, 10)) : null,
        end_date: row.end_date ? parseDateAsLocal(String(row.end_date).slice(0, 10)) : null
      });
      if (row.file_path && String(row.file_path).trim()) {
        const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
        const path = String(row.file_path).replace(/^\//, "");
        setFileUrl(path ? `${baseUrl}/${path}` : "");
      } else {
        setFileUrl("");
        setFile(null);
        setFileType(null);
      }
    } else {
      reset({
        registration_number: "",
        supplier_id: "",
        product: "",
        start_date: null,
        end_date: null
      });
      setFileUrl("");
      setFile(null);
      setFileType(null);
    }
  };

  const handleFileChange = (newFile) => {
    if (fileUrl && fileUrl.startsWith("blob:")) URL.revokeObjectURL(fileUrl);

    if (newFile) {
      const previewUrl = URL.createObjectURL(newFile);
      setFile(newFile);
      setFileUrl(previewUrl);
      setFileType(newFile.type?.includes("pdf") ? "pdf" : "image");
    } else {
      setFile(null);
      setFileUrl("");
      setFileType(null);
    }
  };

  const safeHandleClose = () => {
    if (fileUrl && fileUrl.startsWith("blob:")) URL.revokeObjectURL(fileUrl);
    setFileUrl("");
    setFile(null);
    setFileType(null);
    handleClose();
  };

  useEffect(() => {
    getSelectSuppliers();
    if (open) {
      setModalValues();
    }
  }, [open, row, reset]);

  return (
    <ModalComponent
      open={open}
      handleClose={safeHandleClose}
      title={""}
      actions={
        <div className="HContainer j-center w-complete">
          <LaddaButton variant="contained" onClick={openVerificationModal}>
            <div className="HContainer align-center">
              <p className="button_text">
                {row === null
                  ? "Crear Registro de Importación"
                  : "Actualizar Registro de Importación"}
              </p>
            </div>
          </LaddaButton>
        </div>
      }
    >
      <h1 className="title-bio-details-chard">
        {row === null
          ? "Crear Registro de Importación"
          : "Actualizar Registro de Importación"}
      </h1>

      <form className="VContainer form-modal  ">
        <Grid container>
          <Grid item xs={12} md={12}>
            <p className="create-label ml-0">Número de Registro:</p>

            <FormTextField
              name="registration_number"
              control={control}
              isTextArea={true}
              error={errors.registration_number}
              maxLength={100}
              margin={"0"}
              rows={1}
              width={"auto"}
            />
          </Grid>
          <Grid justifyContent="center" container spacing={1.5}>
            <Grid item xs={!isEdit ? 10 : 12} md={!isEdit ? 5 : 6}>
              <div>
                <p className="create-label ml-0">Proveedor:</p>
                <SimpleSelect
                  name="supplier_id"
                  control={control}
                  label=""
                  options={selectData}
                  margin={"0"}
                  disabled={isEdit}
                  error={errors.supplier_id}
                />
              </div>
            </Grid>
            {!isEdit && (
              <Grid
                item
                xs={2}
                md={1}
                container
                direction="row"
                sx={{
                  alignItems: "flex-end"
                }}
              >
                <LaddaButton
                  variant="contained"
                  onClick={() => setAddSuppliersModal(true)}
                  margin={"0"}
                >
                  <div className="IHContainer align-center">
                    <img className="btn-icon" src={icons.plusIcon} alt="icon" />
                  </div>
                </LaddaButton>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">Producto:</p>
              <FormTextField
                name="product"
                control={control}
                isTextArea={true}
                error={errors.product}
                maxLength={100}
                margin={"0"}
                rows={1}
                width={"auto"}
                disabled={isEdit}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">Inicio de Vigencia:</p>
              <FormCalendar
                name="start_date"
                control={control}
                width={"100%"}
                formLabel=""
                error={errors.start_date}
                margin={"0"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <p className="create-label ml-0"> Fin de Vigencia:</p>
              <FormCalendar
                name="end_date"
                control={control}
                width={"100%"}
                formLabel=""
                error={errors.end_date}
                margin={"0"}
              />
            </Grid>

            <Grid item xs={12}>
              <p className="create-label ml-0">Anexo*</p>
              <FileUploadButton
                onFileChange={handleFileChange}
                path={fileUrl}
                buttonText="Subir archivo (PDF o imagen)"
                fileType={fileType}
              />
            </Grid>
          </Grid>
        </Grid>
      </form>
      <AddSupplier
        open={addSuppliersModal}
        close={() => setAddSuppliersModal(false)}
        supplierOptions={selectData}
        setSupplierOptions={getSelectSuppliers}
      />
      <ForeignTradeVerificationModal
        open={openVerification}
        data={verificationData}
        handleClose={() => setOpenVerification(false)}
        onError={(message) => {
          setOpenVerification(false);
          onShowToast?.("error", message);
        }}
        onConfirm={() => {
          setOpenVerification(false);
          onSubmit();
        }}
        onCancel={() => setOpenVerification(false)}
        isEdit={isEdit}
      />
      <ForeignTradeUpdateWarningModal
        open={existingCheck.open}
        handleClose={() => setExistingCheck({ open: false, data: null })}
        data={existingCheck.data}
        onProceed={submitFromModal}
      />
    </ModalComponent>
  );
};

export default ImportRegistrationCreateUpdateModal;
